import { Card, CardContent } from "../../ui/card"
import { SummaryHeader } from "../../ui/SummaryHeader"
import { useChartColors } from "../../../hooks/useChartColors"
import SalesLoader from "../../../components/loaders/SalesLoader"
import { endOfMonth, format, startOfMonth } from "date-fns";
import { useMemo } from "react"
import { useWholesaleSales } from "../hooks/useSalesWholesale";
import { DataTable } from "../../ui/data-table/data-table"
import { formatNumber } from "../../../utils/formatNumber";
import { formatCurrency } from "../../../utils/formatCurrency";


interface StyleColorPerformanceProps {
  selectedMonth: string;
}


interface StyleColorPerformanceMetrics {
  style_color: string,
  style: string,
  color: string,
  style_description: string,
  color_description: string,
  units_sold: number,
  revenue: number,
  average_order: number
}

export function WholesaleStyleColorPerformance({ selectedMonth }: StyleColorPerformanceProps) {

  const monthDate = useMemo(() => {
    const [year, month] = selectedMonth.split('-').map(Number);
    return new Date(year, month - 1, 1);
  }, [selectedMonth]);

  const startDate = format(startOfMonth(monthDate), "yyyy-MM-dd");
  const endDate = format(endOfMonth(monthDate), "yyyy-MM-dd");


  const { data, isLoading } = useWholesaleSales(startDate, endDate);

  const wholesaleData: StyleColorPerformanceMetrics[] = useMemo(() =>
    (data?.wholesale_style_color_performance || []).map((cat) => ({
      style_color: cat.style_color,
      style: cat.style,
      color: cat.color,
      style_description: cat.style_description,
      color_description: cat.color_description,
      units_sold: cat.units_sold,
      revenue: cat.revenue,
      average_order: cat.average_order,
    }))
    , [data]);

  const noData = wholesaleData.length === 0;


  const columns = [
  {
    accessorKey: "style",
    header: "Style",
    cell: (info: any) => info.getValue(),
  },
  {
    accessorKey: "style_description",
    header: "Style Description",
    cell: (info: any) => info.getValue(),
  },
  {
    accessorKey: "color",
    header: "Color",
    cell: (info: any) => info.getValue(),
  },
  {
    accessorKey: "color_description",
    header: "Color Description",
    cell: (info: any) => info.getValue(),
  },
  {
    accessorKey: "units_sold",
    header: "Units Sold",
    isNumber: true,
    cell: (info: any) => formatNumber(info.getValue()),
  },
  {
    accessorKey: "average_order",
    header: "AOV",
    isNumber: true,
    cell: (info: any) => formatCurrency(info.getValue()),
  },
  {
    accessorKey: "revenue",
    header: "Revenue",
    isNumber: true,
    cell: (info: any) => formatCurrency(info.getValue()),
  },
];

  const colors = useChartColors("wholesale")
  if (!colors) return null;
  return (
    <>
      <SummaryHeader
        title="Style/Color"
      />
      <Card>
        <CardContent>      
                {isLoading ? (
                  <SalesLoader variant="performance-channel" />
                ) : noData ? (
                  <div className="p-4 text-center text-sm text-muted-foreground">
                    No Data Available
                  </div>
                ) : (
                  <DataTable
                    columns={columns}
                    data={wholesaleData}
                    searchColumn="style"
                    searchPlaceholder="Search by Style..."
                    showRowsPerPage={false}
                    initialPageSize={5}
                    showTotal={true} // Enable totals row
                    totalColumns={["revenue"]} // Columns to total
                  />
                )}
        </CardContent>
      </Card>
    </>
  )
}
